// tslint:disable:variable-name
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { PaginatorState } from '../models/paginator.model';
import { ITableState, TableResponseModel } from '../models/table.model';
import { BaseModel } from '../models/base.model';
import { SortState } from '../models/sort.model';
import { GroupingState } from '../models/grouping.model';
import { environment } from '../../../../../environments/environment';
import { AuthModel } from '../../../../modules/auth/_models/auth.model';
import { isConstructorDeclaration } from 'typescript';
import { AuthService } from '../../../../modules/auth/_services/auth.service';

const DEFAULT_STATE: ITableState = {
  filter: {},
  paginator: new PaginatorState(),
  sorting: new SortState(),
  searchTerm: '',
  grouping: new GroupingState(),
  entityId: undefined
};

var token : string = '';
var httpHeaders : HttpHeaders;

export abstract class TableService<T> {
  // Private fields
  private _items$ = new BehaviorSubject<T[]>([]);
  private _items2$ = new BehaviorSubject<T[]>([]); // MC FID-364 
  private _items_ws$ = new BehaviorSubject<T[]>([]);
  private _items_ws2$ = new BehaviorSubject<T[]>([]); // MC FID-364
  private _isLoading$ = new BehaviorSubject<boolean>(false);
  private _isLoading2$ = new BehaviorSubject<boolean>(false); // MC FID-364
  private _isFirstLoading$ = new BehaviorSubject<boolean>(true);
  private _tableState$ = new BehaviorSubject<ITableState>(DEFAULT_STATE);
  private _errorMessage = new BehaviorSubject<string>('');
  private _subscriptions: Subscription[] = [];

  
  // Getters
  get items$() {
    return this._items$.asObservable();
  }
  get items_ws$() {
    return this._items_ws$.asObservable();
  }
  get isLoading$() {
    return this._isLoading$.asObservable();
  }

  // MC FID-364 
  get items2$() {
    return this._items2$.asObservable();
  }
  get items_ws2$() {
    return this._items_ws2$.asObservable();
  }
  get isLoading2$() {
    return this._isLoading2$.asObservable();
  }

  get isFirstLoading$() {
    return this._isFirstLoading$.asObservable();
  }
  get errorMessage$() {
    return this._errorMessage.asObservable();
  }
  get subscriptions() {
    return this._subscriptions;
  }
  // State getters
  get paginator() {
    return this._tableState$.value.paginator;
  }
  get filter() {
    return this._tableState$.value.filter;
  }
  get sorting() {
    return this._tableState$.value.sorting;
  }
  get searchTerm() {
    return this._tableState$.value.searchTerm;
  }
  get grouping() {
    return this._tableState$.value.grouping;
  }
  get tableState$() {
    return this._tableState$;
  }

  protected http: HttpClient;
  protected auths:AuthService;
  // API URL has to be overrided
  API_URL = `${environment.apiUrl}/endpoint`;
  
  constructor(http: HttpClient, auths:AuthService = null) {
    this.http = http;
    this.auths = auths;
    const auth = this.getAuthFromLocalStorage();
    if (auth && auth.access_token){
      httpHeaders = new HttpHeaders({
        Authorization: `Bearer ${auth.access_token}`,
      });
    }
  }

  // CREATE
  // server should return the object with ID
  create(item: BaseModel): Observable<BaseModel> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    return this.http.post<BaseModel>(this.API_URL, item,{headers: httpHeaders,}).pipe(
      catchError(err => {
        this._errorMessage.next(err);
        console.error('CREATE ITEM', err);
        return of({ id: undefined, err: err});
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  create_fd(fd: FormData): Observable<BaseModel> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    return this.http.post<BaseModel>(this.API_URL, fd,{headers: httpHeaders,}).pipe(
      catchError(err => {
        this.isAuthenticationError(err);
        this._errorMessage.next(err);
        console.error('CREATE ITEM', err);
        return of({ id: undefined, err: err});
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  // READ (Returning filtered list of entities)
  find(tableState: ITableState): Observable<TableResponseModel<T>> {
    const url = this.API_URL + '/find';
    this._errorMessage.next('');
    return this.http.post<TableResponseModel<T>>(url, tableState).pipe(
      catchError(err => {
        this._errorMessage.next(err);
        console.error('FIND ITEMS', err);
        return of({ items: [], items_ws: [],total: 0 });
      })
    );
  }

  findWithParams(tableState: ITableState, params): Observable<TableResponseModel<T>> {
    return this.find(tableState);
  }

  getItemById(id: number): Observable<any> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const url = `${this.API_URL}/${id}`;
    return this.http.get<BaseModel>(url,{headers: httpHeaders,}).pipe(
      catchError(err => {
        this._errorMessage.next(err);
        console.error('GET ITEM BY IT', id, err);     
        return of({ id: undefined, err: err});
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  // UPDATE
  update(item: BaseModel): Observable<any> {
    const url = `${this.API_URL}/${item.id}`;
    this._isLoading$.next(true);
    this._errorMessage.next('');
    return this.http.put(url, item,{headers: httpHeaders,} ).pipe(
      catchError(err => {
        this.isAuthenticationError(err);
        this._errorMessage.next(err);
        console.error('UPDATE ITEM', item, err);
        return of({ id: item.id, err: err});
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  // UPDATE Status
  updateStatusForItems(ids: number[], status: number): Observable<any> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const body = { ids, status };
    const url = this.API_URL + '/updateStatus';
    return this.http.put(url, body).pipe(
      catchError(err => {
        this._errorMessage.next(err);
        console.error('UPDATE STATUS FOR SELECTED ITEMS', ids, status, err);
        return of([]);
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  // DELETE
  delete(id: any): Observable<any> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const url = `${this.API_URL}/${id}`;
    return this.http.delete(url,{headers: httpHeaders,}).pipe(
      catchError(err => {
        this.isAuthenticationError(err);
        this._errorMessage.next(err);
        console.error('DELETE ITEM', id, err);
        return of(err);
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  // delete list of items
  deleteItems(ids: number[] = []): Observable<any> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const url = this.API_URL + '/deleteItems';
    const body = { ids };
    return this.http.put(url, body).pipe(
      catchError(err => {
        this._errorMessage.next(err);
        console.error('DELETE SELECTED ITEMS', ids, err);
        return of([]);
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  public fetch() {
    this._isLoading$.next(true);
    this._errorMessage.next('');
   // const request = this.find(this._tableState$.value)
   const request = this.find(this.tableState$.value)
      .pipe(
        tap((res: TableResponseModel<T>) => {
          this._items$.next(res.items);
          this._items_ws$.next(res.items_ws);
          this.patchStateWithoutFetch({
            //paginator: this._tableState$.value.paginator.recalculatePaginator(
              paginator: this.tableState$.value.paginator.recalculatePaginator(
              res.total
            ),
          });
        }),
        catchError((err) => {
          this.isAuthenticationError(err);
          this._errorMessage.next(err);
          return of({
            items: [],
            items_ws: [],
            total: 0
          });
        }),
        finalize(() => {
          this._isLoading$.next(false);
          const itemIds = this._items$.value.map((el: T) => {
            const item = (el as unknown) as BaseModel;
            return item.id;
          });
          this.patchStateWithoutFetch({
            grouping: this._tableState$.value.grouping.clearRows(itemIds),
          });
        })
      )
      .subscribe();
    this._subscriptions.push(request);
  }

  // MC FID-364 
  public fetch2() {
    this._isLoading2$.next(true);
    this._errorMessage.next('');
   // const request = this.find(this._tableState$.value)
   const request = this.find(this.tableState$.value)
      .pipe(
        tap((res: TableResponseModel<T>) => {
          this._items2$.next(res.items);
          this._items_ws2$.next(res.items_ws);
          this.patchStateWithoutFetch({
            //paginator: this._tableState$.value.paginator.recalculatePaginator(
              paginator: this.tableState$.value.paginator.recalculatePaginator(
              res.total
            ),
          });
        }),
        catchError((err) => {
          this.isAuthenticationError(err);
          this._errorMessage.next(err);
          return of({
            items: [],
            items_ws: [],
            total: 0
          });
        }),
        finalize(() => {
          this._isLoading2$.next(false);
          const itemIds = this._items2$.value.map((el: T) => {
            const item = (el as unknown) as BaseModel;
            return item.id;
          });
          this.patchStateWithoutFetch({
            grouping: this._tableState$.value.grouping.clearRows(itemIds),
          });
        })
      )
      .subscribe();
    this._subscriptions.push(request);
  }

  public fetchWithParams(params){
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const request = this.findWithParams(this._tableState$.value, params)
      .pipe(
        tap((res: TableResponseModel<T>) => {
          this._items$.next(res.items);
          this.patchStateWithoutFetch({
            paginator: this._tableState$.value.paginator.recalculatePaginator(
              res.total
            ),
          });
        }),
        catchError((err) => {
          this.isAuthenticationError(err);
          this._errorMessage.next(err);
          return of({
            items: [],
            total: 0
          });
        }),
        finalize(() => {
          this._isLoading$.next(false);
          const itemIds = this._items$.value.map((el: T) => {
            const item = (el as unknown) as BaseModel;
            return item.id;
          });
          this.patchStateWithoutFetch({
            grouping: this._tableState$.value.grouping.clearRows(itemIds),
          });
        })
      )
      .subscribe();
    this._subscriptions.push(request); 
  }


  public setDefaults() {
    this.patchStateWithoutFetch({ filter: {} });
    this.patchStateWithoutFetch({ sorting: new SortState() });
    this.patchStateWithoutFetch({ grouping: new GroupingState() });
    this.patchStateWithoutFetch({ searchTerm: '' });
    this.patchStateWithoutFetch({
      paginator: new PaginatorState()
    });
    this._isFirstLoading$.next(true);
    this._isLoading$.next(true);
    this._tableState$.next(DEFAULT_STATE);
    this._errorMessage.next('');
  }

  // Base Methods
  public patchState(patch: Partial<ITableState>) {
    this.patchStateWithoutFetch(patch);
    this.fetch();
  }

  public patchStateWithoutFetch(patch: Partial<ITableState>) {
    const newState = Object.assign(this._tableState$.value, patch);
    this._tableState$.next(newState);
  }

  public getAuthFromLocalStorage(): AuthModel {
    const ahora = new Date();
    try {
      const authData = JSON.parse(
        localStorage.getItem(`${environment.appVersion}-${environment.USERDATA_KEY}`)
      );
      
      if (!authData){
        this.logoutUser();
      }else{
        let fechaExp = new Date(authData.expires_at)
        if (fechaExp <= ahora){
            this.logoutUser();
        }
      }
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  public logoutUser(){
    if (this.auths){
      this.auths.logout();
      document.location.reload(); 
    }
  }

  public reiniciar(){
    this._items$ = undefined;
    this._items$ = new BehaviorSubject<T[]>([]);
  }

  public reiniciar_ws(){
    this._items_ws$ = undefined;
    this._items_ws$ = new BehaviorSubject<T[]>([]);
  }

  public reiniciar_ts(){
    this.tableState$.next(DEFAULT_STATE);
  }

  // UPDATE
  updateWithoutId(item: any): Observable<any> {
    const url = `${this.API_URL}`;
    this._isLoading$.next(true);
    this._errorMessage.next('');
    return this.http.put(url, item,{headers: httpHeaders,} ).pipe(
      catchError(err => {
        this._errorMessage.next(err);
        console.error('UPDATE ITEM', item, err);
        return of({ id: item.id, err: err});
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  isAuthenticationError(err){
    if (err.error){
      if (err.status === 401){
        this.logoutUser();
      }
    }
  }

  loadingFalse(){
    this._isLoading$.next(false);
  }

}
